import React, { useState } from 'react';
import { Star, X, Send, CheckCircle } from 'lucide-react';
import { useCountry } from '../../contexts/CountryContext';

const API = process.env.REACT_APP_BACKEND_URL;

/** Rate the app + suggest a feature. */
const RateAppModal = ({ user, onClose }) => {
  const ctx = useCountry();
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [suggestion, setSuggestion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    if (!rating && !suggestion.trim()) return;
    setSubmitting(true);
    try {
      await fetch(`${API}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating: rating || null,
          suggestion: suggestion.trim(),
          category: suggestion.trim() ? 'feature' : 'rating',
          user_id: user?.id || null,
          role: user?.role || null,
          country: ctx?.detectedIso3 || null,
        }),
      });
      setDone(true);
      setTimeout(() => onClose?.(), 1600);
    } catch {
      setDone(true);
      setTimeout(() => onClose?.(), 1200);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[95] bg-black/60 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="rate-app-modal">
      <div className="w-full max-w-md bg-white rounded-t-3xl sm:rounded-3xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-bold text-gray-900">Enjoying Ride Yachangu?</h3>
          <button onClick={onClose} data-testid="rate-close" className="p-1.5 rounded-full hover:bg-gray-100"><X className="w-5 h-5" /></button>
        </div>
        {done ? (
          <div className="py-8 text-center" data-testid="rate-done">
            <CheckCircle className="w-12 h-12 text-emerald-500 mx-auto mb-2" />
            <p className="font-semibold text-gray-900">Thank you for your feedback!</p>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-4">Tap a star to rate us, and tell us what to build next.</p>
            <div className="flex justify-center gap-2 mb-5">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  data-testid={`rate-star-${n}`}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  onClick={() => setRating(n)}
                  className="transition-transform active:scale-90"
                >
                  <Star className={`w-9 h-9 ${(hover || rating) >= n ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
                </button>
              ))}
            </div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">Suggest a feature (optional)</label>
            <textarea
              value={suggestion}
              onChange={(e) => setSuggestion(e.target.value)}
              rows={3}
              data-testid="rate-suggestion"
              placeholder="I'd love it if the app could…"
              className="w-full rounded-xl border border-gray-200 p-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 resize-none"
            />
            <button
              onClick={submit}
              disabled={submitting || (!rating && !suggestion.trim())}
              data-testid="rate-submit"
              className="w-full mt-4 h-12 rounded-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-300 text-white font-semibold text-sm inline-flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" /> {submitting ? 'Sending…' : 'Send feedback'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default RateAppModal;
